/**
 * Small helpers for working with locale-prefixed paths.
 * Kept framework-agnostic so it can be used from both client components and proxy/edge code.
 */

export function isLocaleSegment(locales: readonly string[], segment: string): boolean {
	return locales.includes(segment);
}

/**
 * Strip ONLY leading locale segments.
 *
 * Examples:
 * - `/bg/about` -> `/about`
 * - `/bg/en/en/about` -> `/about`
 * - `/about` -> `/about`
 */
export function stripLeadingLocales(pathname: string, locales: readonly string[]): string {
	const segments = pathname.split('/').filter(Boolean);
	let i = 0;
	while (i < segments.length && isLocaleSegment(locales, segments[i]!)) {
		i++;
	}
	const rest = segments.slice(i);
	return `/${rest.join('/')}`.replace(/\/+/g, '/') || '/';
}

/**
 * Canonicalize paths that have multiple leading locale segments by collapsing to the last one.
 *
 * Examples:
 * - `/bg/en/en/about` -> `/en/about`
 * - `/bg/en` -> `/en`
 *
 * If there is 0 or 1 leading locale segment, returns null (no redirect).
 */
export function canonicalizeLeadingLocales(
	pathname: string,
	locales: readonly string[]
): string | null {
	const segments = pathname.split('/').filter(Boolean);

	let i = 0;
	while (i < segments.length && isLocaleSegment(locales, segments[i]!)) {
		i++;
	}

	// 0 or 1 locale prefix is already canonical
	if (i <= 1) {
		return null;
	}

	const canonicalLocale = segments[i - 1]!;
	const rest = segments.slice(i);
	return `/${canonicalLocale}${rest.length ? `/${rest.join('/')}` : ''}`;
}

